// utils
import { fetcher, endpoints, poster } from '../utils/axios';

/**
 * Retrieves the details of a company.
 * @param {Object} payload - The object containing the query parameters for the request.
 * @param {string} payload.id - The id of the company to fetch.
 * @returns {Promise} - A promise that resolves or rejects based on the response from the server.
 */
export async function getCompanyDetails(payload) {
    const URL = endpoints.company.getCompanyDetails;
    return await fetcher([URL,{params:payload}]);
}

/**
 * Retrieves the list of staff for a company.
 * @param {Object} payload - The object containing the query parameters for the request.
 * @param {string} payload.id - The id of the company to fetch the staff for.
 * @returns {Promise} - A promise that resolves or rejects based on the response from the server.
 */
export async function getCompanyStaffList(payload){
    const URL = endpoints.company.getCompanyStaffList;
    return await fetcher([URL, {params:payload}])
}

/**
 * Retrieves the details of a staff member for a company.
 * @param {Object} payload - The object containing the query parameters for the request.
 * @param {string} payload.id - The id of the staff member to fetch.
 * @returns {Promise} - A promise that resolves or rejects based on the response from the server.
 */
export async function getCompanyStaffDetails(payload){
    const URL = endpoints.company.getCompanyStaffDetails;
    return await fetcher([URL, {params:payload}])
}

/**
 * Retrieves the list of companies.
 * @param {Object} payload - The object containing the query parameters for the request.
 * @param {string} [payload.search] - The search query for the companies.
 * @param {number} [payload.page] - The page to fetch of the companies.
 * @param {number} [payload.limit] - The number of companies to fetch per page.
 * @returns {Promise} - A promise that resolves or rejects based on the response from the server.
 */
export async function getCompanyList(payload){
    const URL = endpoints.company.getCompanyList;
    return await fetcher([URL, {params:payload}])
}

/**
 * Creates a new company.
 * @param {Object} payload - The object containing the company information to add.
 * @param {string} payload.name - The name of the company.
 * @param {string} [payload.email] - The email address of the company.
 * @param {string} [payload.phone_number] - The phone number of the company.
 * @param {string} [payload.address] - The address of the company.
 * @param {string} [payload.industry_sector] - The industry sector of the company.
 * @param {string} [payload.description] - The description of the company.
 * @param {string} [payload.website] - The website of the company.
 * @param {string} [payload.logo] - The logo of the company.
 * @returns {Promise} - A promise that resolves or rejects based on the response from the server.
 */
export async function createCompany(payload){
    const URL = endpoints.company.createCompany;
    return await poster([URL,payload])
}

/**
 * Adds a new staff member to a company.
 * @param {Object} payload - The object containing the staff member information to add.
 * @param {string} payload.name - The name of the staff member.
 * @param {string} [payload.email] - The email address of the staff member.
 * @param {string} [payload.phone_number] - The phone number of the staff member.
 * @param {string} [payload.designation] - The designation of the staff member.
 * @param {string} [payload.business_function] - The business function of the staff member.
 * @returns {Promise} - A promise that resolves or rejects based on the response from the server.
 */
export async function addStaff(payload){
    const URL = endpoints.company.addStaff;
    return await poster([URL,payload])
}

/**
 * Updates the details of a company.
 * @param {Object} payload - The object containing the updated company information.
 * @param {string} payload.id - The id of the company to update.
 * @param {string} [payload.name] - The name of the company.
 * @param {string} [payload.email] - The email address of the company.
 * @param {string} [payload.phone_number] - The phone number of the company.
 * @param {string} [payload.address] - The address of the company.
 * @param {string} [payload.industry_sector] - The industry sector of the company.
 * @param {string} [payload.description] - The description of the company.
 * @param {string} [payload.website] - The website of the company.
 * @param {string} [payload.logo] - The logo of the company.
 * @returns {Promise} - A promise that resolves or rejects based on the response from the server.
 */

export async function updateCompany(payload){
    const URL = endpoints.company.updateCompany;
    return await poster([URL, payload]);
}
/**
 * Updates the details of a staff member for a company.
 * @param {Object} payload - The object containing the updated staff member information.
 * @param {string} payload.id - The id of the staff member to update.
 * @param {string} [payload.name] - The name of the staff member.
 * @param {string} [payload.email] - The email address of the staff member.
 * @param {string} [payload.phone_number] - The phone number of the staff member.
 * @param {string} [payload.designation] - The designation of the staff member.
 * @param {string} [payload.business_function] - The business function of the staff member.
 * @returns {Promise} - A promise that resolves or rejects based on the response from the server.
 */
export async function updateCompanyStaff(payload){
    const URL = endpoints.company.updateCompanyStaff;
    return await poster([URL, payload]);
} 

/**
 * Deletes a staff member for a company.
 * @param {Object} payload - The object containing the query parameters for the request.
 * @param {string} payload.id - The id of the staff member to delete.
 * @returns {Promise} - A promise that resolves or rejects based on the response from the server.
 */
export async function deleteStaff(payload){
    const URL = endpoints.company.deleteStaff;
    return await poster([URL, payload]);
}
